import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createMatch, listTeams } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Plus, X, BookOpen } from "lucide-react";

const FORMATS = [
  { code: "T20", overs: 20 },
  { code: "ODI", overs: 50 },
  { code: "Test", overs: 90 },
];

function PlayerListInput({ players, setPlayers, side }) {
  const update = (i, val) => setPlayers(players.map((p, idx) => (idx === i ? val : p)));
  const remove = (i) => setPlayers(players.filter((_, idx) => idx !== i));
  const add = () => setPlayers([...players, ""]);
  return (
    <div className="space-y-2">
      {players.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="font-mono text-xs text-muted-foreground w-6 stat-tabular">{i + 1}.</span>
          <input
            value={p}
            onChange={(e) => update(i, e.target.value)}
            data-testid={`team-${side}-player-${i}-input`}
            placeholder={`Player ${i + 1}`}
            className="flex-1 bg-transparent border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm font-mono"
          />
          {players.length > 2 && (
            <button
              onClick={() => remove(i)}
              data-testid={`team-${side}-remove-player-${i}`}
              className="p-2 text-muted-foreground hover:text-destructive border border-border hover:border-destructive transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={add}
        data-testid={`team-${side}-add-player-btn`}
        className="text-xs uppercase tracking-[0.2em] font-bold text-primary hover:text-primary/80 inline-flex items-center gap-2 mt-2"
      >
        <Plus className="w-3 h-3" strokeWidth={3} /> Add Player
      </button>
    </div>
  );
}

export default function NewMatch() {
  const nav = useNavigate();
  const [teamA, setTeamA] = useState("Team Alpha");
  const [teamB, setTeamB] = useState("Team Bravo");
  const [playersA, setPlayersA] = useState(["", "", "", "", ""]);
  const [playersB, setPlayersB] = useState(["", "", "", "", ""]);
  const [format, setFormat] = useState("T20");
  const [overs, setOvers] = useState(20);
  const [tossWinner, setTossWinner] = useState("A");
  const [tossDecision, setTossDecision] = useState("bat");
  const [venue, setVenue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savedTeams, setSavedTeams] = useState([]);

  useEffect(() => {
    listTeams().then(setSavedTeams).catch(() => setSavedTeams([]));
  }, []);

  const applySavedTeam = (side, teamId) => {
    if (!teamId) return;
    const t = savedTeams.find((x) => x.id === teamId);
    if (!t) return;
    const names = t.players.map((p) => p.name);
    if (side === "A") {
      setTeamA(t.name);
      setPlayersA(names.length ? names : [""]);
    } else {
      setTeamB(t.name);
      setPlayersB(names.length ? names : [""]);
    }
    toast.success(`Loaded ${t.name}`);
  };

  const submit = async () => {
    const filteredA = playersA.filter((p) => p.trim());
    const filteredB = playersB.filter((p) => p.trim());
    if (!teamA.trim() || !teamB.trim()) {
      toast.error("Please enter both team names");
      return;
    }
    if (filteredA.length < 2 || filteredB.length < 2) {
      toast.error("Each team needs at least 2 players");
      return;
    }
    try {
      setSubmitting(true);
      const match = await createMatch({
        team_a_name: teamA,
        team_a_players: filteredA,
        team_b_name: teamB,
        team_b_players: filteredB,
        format,
        overs: Number(overs),
        toss_winner: tossWinner,
        toss_decision: tossDecision,
        venue: venue || null,
      });
      toast.success("Match created");
      nav(`/match/${match.id}/score`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create match");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="new-match-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Step 01</p>
        <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2">
          Set up the match
        </h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-xl">
          Configure teams, format and toss. You can adjust batting order on the live scoring screen.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Team A */}
        <div className="border border-border p-6 bg-card">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-xs text-muted-foreground uppercase tracking-[0.2em]">Team A</span>
            <span className="text-primary font-display font-black">A</span>
          </div>
          {savedTeams.length > 0 && (
            <SavedTeamPicker savedTeams={savedTeams} onPick={(id) => applySavedTeam("A", id)} side="a" />
          )}
          <input
            value={teamA}
            onChange={(e) => setTeamA(e.target.value)}
            data-testid="team-a-name-input"
            className="w-full bg-transparent border-b border-border focus:border-primary focus:outline-none pb-2 mb-5 font-display font-black uppercase text-2xl tracking-tight"
          />
          <PlayerListInput players={playersA} setPlayers={setPlayersA} side="a" />
        </div>

        {/* Team B */}
        <div className="border border-border p-6 bg-card">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-xs text-muted-foreground uppercase tracking-[0.2em]">Team B</span>
            <span className="text-primary font-display font-black">B</span>
          </div>
          {savedTeams.length > 0 && (
            <SavedTeamPicker savedTeams={savedTeams} onPick={(id) => applySavedTeam("B", id)} side="b" />
          )}
          <input
            value={teamB}
            onChange={(e) => setTeamB(e.target.value)}
            data-testid="team-b-name-input"
            className="w-full bg-transparent border-b border-border focus:border-primary focus:outline-none pb-2 mb-5 font-display font-black uppercase text-2xl tracking-tight"
          />
          <PlayerListInput players={playersB} setPlayers={setPlayersB} side="b" />
        </div>
      </div>

      {/* Format & overs */}
      <div className="border border-border p-6 bg-card mt-6">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Format</label>
            <div className="grid grid-cols-3 gap-2 mt-3">
              {FORMATS.map((f) => (
                <button
                  key={f.code}
                  onClick={() => {
                    setFormat(f.code);
                    setOvers(f.overs);
                  }}
                  data-testid={`format-${f.code.toLowerCase()}-btn`}
                  className={`tactical-btn py-3 text-sm border ${
                    format === f.code
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:border-primary"
                  }`}
                >
                  {f.code}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Overs / innings</label>
            <input
              type="number"
              min={1}
              max={500}
              value={overs}
              onChange={(e) => setOvers(e.target.value)}
              data-testid="overs-input"
              className="w-full bg-transparent border border-border focus:border-primary focus:outline-none px-4 py-3 mt-3 font-mono text-lg stat-tabular"
            />
          </div>
        </div>
      </div>

      {/* Toss */}
      <div className="border border-border p-6 bg-card mt-6">
        <h3 className="font-display font-black uppercase text-xl tracking-tight mb-4">Toss</h3>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Won by</label>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                onClick={() => setTossWinner("A")}
                data-testid="toss-winner-a-btn"
                className={`tactical-btn py-3 text-sm border truncate ${
                  tossWinner === "A" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"
                }`}
              >
                {teamA || "Team A"}
              </button>
              <button
                onClick={() => setTossWinner("B")}
                data-testid="toss-winner-b-btn"
                className={`tactical-btn py-3 text-sm border truncate ${
                  tossWinner === "B" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"
                }`}
              >
                {teamB || "Team B"}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Decision</label>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                onClick={() => setTossDecision("bat")}
                data-testid="toss-decision-bat-btn"
                className={`tactical-btn py-3 text-sm border ${
                  tossDecision === "bat" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"
                }`}
              >
                Bat
              </button>
              <button
                onClick={() => setTossDecision("bowl")}
                data-testid="toss-decision-bowl-btn"
                className={`tactical-btn py-3 text-sm border ${
                  tossDecision === "bowl" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"
                }`}
              >
                Bowl
              </button>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Venue (optional)</label>
          <input
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            data-testid="venue-input"
            placeholder="e.g. Marine Drive Ground"
            className="w-full bg-transparent border border-border focus:border-primary focus:outline-none px-4 py-3 mt-3 font-mono text-sm"
          />
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="create-match-submit-btn"
          className="tactical-btn bg-primary text-primary-foreground hover:bg-[#C9E620] disabled:opacity-50 disabled:cursor-not-allowed px-8 py-4 inline-flex items-center gap-3 text-sm"
        >
          {submitting ? "Creating..." : "Start Match"}
          <ArrowRight className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

function SavedTeamPicker({ savedTeams, onPick, side }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <BookOpen className="w-3.5 h-3.5 text-primary shrink-0" strokeWidth={2.5} />
      <select
        onChange={(e) => { onPick(e.target.value); e.target.value = ""; }}
        defaultValue=""
        data-testid={`team-${side}-saved-picker`}
        className="flex-1 bg-card border border-border focus:border-primary focus:outline-none px-2 py-1.5 text-xs font-mono uppercase tracking-wider"
      >
        <option value="">Pick from saved teams...</option>
        {savedTeams.map((t) => (
          <option key={t.id} value={t.id}>{t.name} ({t.players.length})</option>
        ))}
      </select>
    </div>
  );
}

